import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../config/api";
import { useMyContext } from "../../context/MyContext";
import DashboardLayout from "../layout";
import Header from "../header/Header";
import { Dropdown, Menu, Modal, Space, Table, Tag, Select, Upload, DatePicker } from "antd";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { PiDotsThreeVerticalBold } from "react-icons/pi";
import { HiOutlinePencilSquare } from "react-icons/hi2";
import { UploadOutlined } from "@ant-design/icons";
import InvoicePDF from "../shared/pdf/invoicePDF";
import { getUser } from "../../lib/user";
import { getQuote } from "../../lib/quotes";
import { useState, useEffect } from "react";
import Loader from "../shared/loader/loader";
import Search from "../../icons/Search";
import Download from "../../icons/Downloadicon";
import ExportCSVPopup from "../ExportCSVPopup";
import React, { useMemo } from "react";
import * as XLSX from "xlsx";
import InvoiceModal from "../shared/common/invoiceModal";
import PopUp from "./PopUp";
import EditInvoiceModal from "./EditInvoiceModal";
import CreditInvoiceModal from "./CreditInvoiceModal";
import AdminAuthComponent from "./AdminAuthComponent";
import CardsContainer from "./CardsContainer";
import { useLocation } from "react-router-dom";
import { useSearchParams } from "react-router-dom";

// Helper function to check if user is admin
function isAdmin(role) {
  if (!role) return false;

  // Handle both string and array formats of role
  if (typeof role === 'string') {
    // Check if role contains 'admin' substring
    return role.toLowerCase().includes('admin');
  }

  if (Array.isArray(role)) {
    // Check if any element in array contains 'admin'
    return role.some(r => r.toLowerCase().includes('admin'));
  }

  return false;
}

const formatInvoiceCurrency = (amount, currency = "USD") => {
  const numericAmount = Number(amount);

  if (Number.isNaN(numericAmount)) {
    return "N/A";
  }

  const normalizedCurrency = String(currency || "USD").toUpperCase() === "EUR" ? "EUR" : "USD";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: normalizedCurrency,
    minimumFractionDigits: 2,
  }).format(numericAmount);
};

const isExternalInvoice = (invoice) =>
  Boolean(invoice?.isExternalInvoice || invoice?.invoiceSource === "external");

const isImportInvoice = (invoice) =>
  Boolean(invoice?.isImportInvoice || invoice?.invoiceSource === "import");

const isCreditInvoiceRecord = (invoice) => {
  if (invoice?.isCreditInvoice || invoice?.documentType === "credit") return true;
  // Fallback for older credit rows saved without the flag
  if (Number(invoice?.total) < 0) return true;
  if (Number(invoice?.remaining) < 0) return true;
  if (Array.isArray(invoice?.items) && invoice.items.some((item) => Number(item?.qty) < 0 || Number(item?.price) < 0)) {
    return true;
  }
  return false;
};

/** Credit invoices display monetary values as negative */
const getCreditSignedAmount = (invoice, amount) => {
  const numeric = Number(amount || 0);
  if (Number.isNaN(numeric)) return 0;
  if (isCreditInvoiceRecord(invoice)) return -Math.abs(numeric);
  return numeric;
};

const hasEditableMongoName = (invoice) =>
  Boolean(
    String(invoice?.name || "").trim() &&
    (isExternalInvoice(invoice) || isImportInvoice(invoice) || !invoice?.userId)
  );

const normalizeUserFilterValue = (value) =>
  String(value || "").trim().toLowerCase();

const MONTH_OPTIONS = [
  { value: 0, label: "January" },
  { value: 1, label: "February" },
  { value: 2, label: "March" },
  { value: 3, label: "April" },
  { value: 4, label: "May" },
  { value: 5, label: "June" },
  { value: 6, label: "July" },
  { value: 7, label: "August" },
  { value: 8, label: "September" },
  { value: 9, label: "October" },
  { value: 10, label: "November" },
  { value: 11, label: "December" },
];

const getInvoiceDate = (invoice) => {
  const rawDate = invoice?.date || invoice?.invoiceDate || invoice?.createdAt;
  if (!rawDate) return null;
  const d = dayjs(rawDate);
  if (d.isValid()) return d;
  const parsed = new Date(rawDate);
  if (!Number.isNaN(parsed.getTime())) {
    const d2 = dayjs(parsed);
    if (d2.isValid()) return d2;
  }
  return null;
};

const normalizeImportHeader = (header) =>
  String(header || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const INVOICE_IMPORT_FIELD_ALIASES = {
  invoiceno: "invoiceNo",
  invoicenumber: "invoiceNo",
  orderno: "orderNumber",
  ordernumber: "orderNumber",
  invoicedate: "invoiceDate",
  duedays: "dueDays",
  name: "name",
  username: "name",
  clientname: "name",
  companyname: "companyName",
  company: "companyName",
  customername: "customerName",
  businessname: "businessName",
  description: "description",
  items: "items",
  lineitems: "items",
  invoiceitems: "items",
  orderreference: "orderReference",
  collection: "collectionLocation",
  collectionlocation: "collectionLocation",
  delivery: "deliveryLocation",
  deliverylocation: "deliveryLocation",
  servicerecipient: "serviceRecipientText",
  servicerecipienttext: "serviceRecipientText",
  billto: "externalBillToText",
  billtotext: "externalBillToText",
  externalbillto: "externalBillToText",
  externalbilltotext: "externalBillToText",
  bottomnote: "bottomNote",
  currency: "currency",
  bankaccount: "bankAccount",
  banklabel: "bankLabel",
  bankaccountname: "bankAccountName",
  bankiban: "bankIban",
  bankswift: "bankSwift",
  total: "total",
  invoiceamount: "total",
  paid: "paid",
  paidamount: "paid",
  amountpaid: "paid",
  status: "status",
};

const INVOICE_IMPORT_TEMPLATE_HEADERS = [
  "Invoice No.",
  "Client Name",
  "Invoice Date",
  "Due Days",
  "Total",
  "Paid",
  "Description",
  "STATUS",
];

const parseImportItems = (itemsValue) => {
  if (!itemsValue) return undefined;

  return String(itemsValue)
    .split(";")
    .map((entry) => {
      const [description, qty, price] = entry.split("|");
      return {
        description: String(description || "").trim(),
        qty: Number(qty || 1),
        price: Number(String(price || 0).replace(/[$,]/g, "")),
      };
    })
    .filter((item) => item.description);
};

const normalizeImportRow = (row) => {
  const normalized = {};

  Object.entries(row).forEach(([key, value]) => {
    const field = INVOICE_IMPORT_FIELD_ALIASES[normalizeImportHeader(key)];
    if (!field) return;
    normalized[field] = typeof value === "string" ? value.trim() : value;
  });

  const parsedItems = parseImportItems(normalized.items);
  if (parsedItems?.length) {
    normalized.items = parsedItems;
  } else {
    delete normalized.items;
  }

  return normalized;
};

const Invoices = () => {
  const { user, token, isAuthenticated } = useMyContext();
  const queryClient = useQueryClient();
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [downloadingInvoices, setDownloadingInvoices] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [userDetails, setUserDetails] = useState({});
  const [invoiceAmounts, setInvoiceAmounts] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [showPopUp, setShowPopUp] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [createCreditInvoiceMode, setCreateCreditInvoiceMode] = useState(false);
  const [highlightedInvoiceId, setHighlightedInvoiceId] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [createInvoiceOpen, setCreateInvoiceOpen] = useState(false);
  const [createInvoiceShipment, setCreateInvoiceShipment] = useState(null);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Admin authentication state
  const [adminAuthenticated, setAdminAuthenticated] = useState(false);

  // Check if current user has admin role
  const userIsAdmin = useMemo(() => isAdmin(user?.role), [user]);

  // Check if admin is already authenticated from session storage
  useEffect(() => {
    if (userIsAdmin) {
      const storedAuth = sessionStorage.getItem("adminAuthenticated");
      if (storedAuth === "true") {
        setAdminAuthenticated(true);
      }
    }
  }, [userIsAdmin]);



  useEffect(() => {
    if (location.state?.highlightInvoiceId) {
      setHighlightedInvoiceId(location.state.highlightInvoiceId);

      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (!highlightedInvoiceId) return;

    const handleClick = () => {
      setHighlightedInvoiceId(null);
    };

    window.addEventListener("click", handleClick);

    return () => {
      window.removeEventListener("click", handleClick);
    };
  }, [highlightedInvoiceId]);

  const { data = [], isLoading, error } = useQuery(
    ["invoices"],
    async () => {
      if (!token) {
        throw new Error("No token available");
      }

      try {
        let response;
        if (userIsAdmin) {
          response = await api.get("/invoice", {
            headers: { authorization: `Bearer ${token}` },
          });
        } else {
          response = await api.get(`/invoice/user/${user?.uid}`, {
            headers: { authorization: `Bearer ${token}` },
          });
        }

        // Transform the data to ensure status and date are accurate at top level
        const transformedData = response.data.map((invoice) => {
          const absPaid = Math.abs(Number(invoice.paid || 0));
          const absRemaining = Math.abs(Number(invoice.remaining || 0));

          let derivedStatus = invoice.status;
          if (absPaid > 0 && absRemaining > 0) {
            derivedStatus = "Partially Paid";
          } else if (absRemaining <= 0 && (absPaid > 0 || Number(invoice.total || 0) !== 0)) {
            derivedStatus = "Paid";
          } else if (absPaid === 0) {
            derivedStatus = "Not Paid";
          }

          return {
            ...invoice,
            status: derivedStatus,
            date:
              invoice.date || invoice.invoiceDate || invoice.createdAt || null,
          };
        });

        return transformedData;
      } catch (error) {
        if (
          error?.response?.status === 401 &&
          error?.response?.data?.error === "You are not authorized to make this request"
        ) {
          window.location.reload();
          return null;
        }
        throw error;
      }
    },
    {
      enabled: !!token && isAuthenticated && (!userIsAdmin || adminAuthenticated),
      retry: 3,
      retryDelay: 1000,
      onSuccess: (data) => {
        // Fetch user details and invoice amounts for each invoice
        fetchUserDetailsAndInvoiceAmounts(data);
      },
      onError: (error) => {
        // console.error("Query failed:", error);
      },
    }
  );

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("highlightInvoices")) || [];

    if (!stored.length || !data?.length) return;

    const matchedIds = data
      .filter(inv => stored.includes(inv.invoiceNo))
      .map(inv => inv._id);

    if (matchedIds.length) {
      setHighlightedInvoiceId(matchedIds);

      setTimeout(() => {
        const row = document.querySelector(".floating-invoice-row");
        if (row) {
          row.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 300);

      localStorage.removeItem("highlightInvoices");
    }
  }, [data]);

  useEffect(() => {
    const invoiceNoFromUrl = searchParams.get("invoiceNo");

    if (!invoiceNoFromUrl) return;
    if (!data || data.length === 0) return;

    const matchedInvoice = data.find(
      (inv) => String(inv.invoiceNo) === String(invoiceNoFromUrl)
    );

    if (matchedInvoice) {
      setHighlightedInvoiceId(matchedInvoice._id);

      // scroll to highlighted row
      setTimeout(() => {
        const row = document.querySelector(".floating-invoice-row");
        if (row) {
          row.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 300);
    }
  }, [data, searchParams]);

  // Function to fetch user details (and map invoice totals locally if needed)
  const fetchUserDetailsAndInvoiceAmounts = async (invoices) => {
    try {
      const userIds = [...new Set(invoices.map(invoice => invoice.userId))];
      const shipmentIds = [...new Set(invoices.map(invoice => invoice.shipmentId).filter(Boolean))];

      // Fetch all users at once
      const userDetailsObj = {};
      await Promise.all(userIds.map(async (userId) => {
        if (userId) {
          try {
            const userData = await getUser(userId);
            userDetailsObj[userId] = userData;
          } catch (error) {
            console.error(`Error fetching user data for ${userId}:`, error);
            userDetailsObj[userId] = null;
          }
        }
      }));

      // Map invoice totals by shipmentId from the invoices list itself
      const invoiceAmountsObj = {};
      invoices.forEach(inv => {
        if (inv?.shipmentId) {
          invoiceAmountsObj[inv.shipmentId] = typeof inv.total === 'number' ? inv.total : "N/A";
        }
      });
      setUserDetails(userDetailsObj);
      setInvoiceAmounts(invoiceAmountsObj);
    } catch (error) {
      console.error("Error fetching user details and invoice amounts:", error);
    }
  };

  const uniqueUsers = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }

    const users = [];
    const userNames = new Set();

    data.forEach(invoice => {
      const displayName = invoice.name || userDetails[invoice.userId]?.name || "";
      const normalizedName = normalizeUserFilterValue(displayName);

      if (normalizedName && !userNames.has(normalizedName)) {
        userNames.add(normalizedName);
        users.push({
          id: normalizedName,
          name: displayName,
        });
      }
    });

    return users.sort((a, b) => a.name.localeCompare(b.name));
  }, [data, userDetails]);

  const availableYears = useMemo(() => {
    const yearsSet = new Set();
    const currentYear = dayjs().year();
    yearsSet.add(currentYear);
    yearsSet.add(currentYear - 1);

    (data || []).forEach((invoice) => {
      const d = getInvoiceDate(invoice);
      if (d) {
        yearsSet.add(d.year());
      }
    });

    return Array.from(yearsSet)
      .sort((a, b) => b - a)
      .map((year) => ({
        value: year,
        label: String(year),
      }));
  }, [data]);

  const filterCounts = useMemo(() => {
    if (!data) return { all: 0, pending: 0, paid: 0, overdue: 0 };
    const today = dayjs().startOf("day");

    let baseList = [...data];
    if (searchQuery.trim() !== "") {
      baseList = baseList.filter((invoice) => {
        const userName = userDetails[invoice.userId]?.name || "";
        const searchFields = [
          invoice.name,
          invoice.invoiceNo,
          invoice.orderNumber,
          invoice.description,
          invoice.status,
          userName,
        ];
        return searchFields.some(
          (field) =>
            field &&
            field.toString().toLowerCase().includes(searchQuery.toLowerCase())
        );
      });
    }
    if (selectedUser) {
      baseList = baseList.filter(
        (invoice) =>
          normalizeUserFilterValue(
            invoice.name || userDetails[invoice.userId]?.name
          ) === selectedUser
      );
    }

    if (selectedMonth !== null && selectedMonth !== undefined) {
      baseList = baseList.filter((invoice) => {
        const d = getInvoiceDate(invoice);
        return d && d.month() === selectedMonth;
      });
    }

    if (selectedYear !== null && selectedYear !== undefined) {
      baseList = baseList.filter((invoice) => {
        const d = getInvoiceDate(invoice);
        return d && d.year() === selectedYear;
      });
    }

    let pending = 0;
    let paid = 0;
    let overdue = 0;

    baseList.forEach((invoice) => {
      const isPaid = invoice.status?.toLowerCase() === "paid";
      const isCredit = isCreditInvoiceRecord(invoice);
      const parsedDueDate = invoice?.dueDate ? dayjs(invoice.dueDate) : null;
      const isPastDue =
        parsedDueDate &&
        parsedDueDate.isValid() &&
        parsedDueDate.startOf("day").isBefore(today);
      const isOverdue = !isPaid && !isCredit && isPastDue;

      if (isPaid) {
        paid++;
      } else if (isOverdue) {
        overdue++;
      } else {
        pending++;
      }
    });

    return {
      all: baseList.length,
      pending,
      paid,
      overdue,
    };
  }, [data, searchQuery, selectedUser, selectedMonth, selectedYear, userDetails]);

  const sortedAndFilteredData = useMemo(() => {
    let filteredData = [...(data || [])];
    const today = dayjs().startOf("day");

    // Apply search filter
    if (searchQuery.trim() !== "") {
      filteredData = filteredData.filter((invoice) => {
        const userName = userDetails[invoice.userId]?.name || "";
        const searchFields = [
          invoice.name,
          invoice.invoiceNo,
          invoice.orderNumber,
          invoice.description,
          invoice.status,
          userName,
        ];

        return searchFields.some(
          (field) =>
            field &&
            field.toString().toLowerCase().includes(searchQuery.toLowerCase())
        );
      });
    }

    if (selectedUser) {
      filteredData = filteredData.filter(
        (invoice) =>
          normalizeUserFilterValue(
            invoice.name || userDetails[invoice.userId]?.name
          ) === selectedUser
      );
    }

    // Apply month filter
    if (selectedMonth !== null && selectedMonth !== undefined) {
      filteredData = filteredData.filter((invoice) => {
        const d = getInvoiceDate(invoice);
        return d && d.month() === selectedMonth;
      });
    }

    // Apply year filter
    if (selectedYear !== null && selectedYear !== undefined) {
      filteredData = filteredData.filter((invoice) => {
        const d = getInvoiceDate(invoice);
        return d && d.year() === selectedYear;
      });
    }

    // Apply status filter (All, Pending, Paid, Overdue)
    if (statusFilter && statusFilter !== "all") {
      filteredData = filteredData.filter((invoice) => {
        const isPaid = invoice.status?.toLowerCase() === "paid";
        const isCredit = isCreditInvoiceRecord(invoice);
        const parsedDueDate = invoice?.dueDate ? dayjs(invoice.dueDate) : null;
        const isPastDue =
          parsedDueDate &&
          parsedDueDate.isValid() &&
          parsedDueDate.startOf("day").isBefore(today);
        const isOverdue = !isPaid && !isCredit && isPastDue;

        if (statusFilter === "paid") return isPaid;
        if (statusFilter === "overdue") return isOverdue;
        if (statusFilter === "pending") return !isPaid && !isOverdue;
        return true;
      });
    }

    // Sort by date
    return filteredData.sort((a, b) => {
      const dateA = a.date || a.invoiceDate || a.createdAt;
      const dateB = b.date || b.invoiceDate || b.createdAt;
      return new Date(dateB) - new Date(dateA);
    });
  }, [data, searchQuery, userDetails, statusFilter, selectedUser, selectedMonth, selectedYear]);

  const sortedData = useMemo(() => {
    return [...(data || [])].sort((a, b) => {
      const dateA = a.date || a.invoiceDate || a.createdAt;
      const dateB = b.date || b.invoiceDate || b.createdAt;

      return new Date(dateB) - new Date(dateA);
    });
  }, [data]);

  const updatePaymentDateMutation = useMutation(
    ["invoice-payment-date-update"],
    async ({ id, lastPaymentDate }) => {
      await api.put(`/invoice/${id}`, { lastPaymentDate });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["invoices"]);
        toast.success("Last Payment Date changed!");
      },
      onError: (err) => {
        console.error(err);
        toast.error("Failed to update payment date");
      }
    }
  );

  // Update status and, if marking as Paid, also reconcile paid/remaining
  const updateStatusMutation = useMutation(
    ["invoice"],
    async ({ id, status }) => {
      const lower = (status || '').toLowerCase();
      if (lower === 'paid' || lower === 'not paid') {
        // Retrieve the latest invoice totals from the cache (fallback to API)
        const all = queryClient.getQueryData(["invoices"]) || [];
        const found = all.find((i) => i._id === id);
        let total = Number(found?.total || 0);

        // Fallback: fetch invoice by id if total not available
        if (!total) {
          try {
            const resp = await api.get(`/invoice/${id}`);
            total = Number(resp?.data?.total || 0);
          } catch (_) { /* ignore and use 0 */ }
        }

        // Compose payload depending on status
        const payload =
          lower === 'paid'
            ? { status: 'Paid', paid: total, remaining: 0, lastPaymentDate: new Date().toISOString() }
            : { status: 'Not Paid', paid: 0, remaining: total, lastPaymentDate: null };

        await api.put(`/invoice/${id}`, payload);
      } else {
        // Only change the status flag for any future statuses
        await api.put(`/invoice/status/${id}/${status}`);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["invoices"]);
        toast.success("Status updated!");
      },
      onError: (err) => {
        console.error(err);
        toast.error("Failed to update status");
      }
    }
  );

  const deleteInvoiceMutation = useMutation(
    ["invoice-delete"],
    async (id) => {
      await api.delete(`/invoice/${id}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["invoices"]);
        toast.success("Invoice deleted!");
      },
    }
  );

  const [needsScroll, setNeedsScroll] = useState(false);
  useEffect(() => {
    const checkWidth = () => {
      setNeedsScroll(window.innerWidth < 1400);
    };
    checkWidth();
    window.addEventListener('resize', checkWidth);
    return () => window.removeEventListener('resize', checkWidth);
  }, []);
  if (!isAuthenticated) {
    return <div>Loading...</div>;
  }

  // Render authentication component for admin users
  if (userIsAdmin && !adminAuthenticated) {
    return (
      <DashboardLayout>
        <Header title="Invoices Authentication" />
        <AdminAuthComponent onAuthenticated={setAdminAuthenticated} />
      </DashboardLayout>
    );
  }

  const getStatusStyles = (status) => {
    switch (status?.toLowerCase()) {
      case "not paid":
        return { borderColor: "red", backgroundColor: "red", color: "white" };
      case "paid":
        return { borderColor: "green", backgroundColor: "green" };
      default:
        return { borderColor: "#000", backgroundColor: "#fff" }; // Default styles
    }
  };

  const taskStatus = ["Paid", "Not Paid"];

  const handleStatusChange = async (id, status, record) => {
    if (status === "Partially Paid") {
      const completeInvoice = { ...record };
      if (parseFloat(record?.remaining || 0) <= 0) {
        toast.warning(`Cannot process partial payment. The remaining amount is ${formatInvoiceCurrency(0, record.currency)}.`);
        return;
      }
      setSelectedInvoice(completeInvoice);
      setShowPopUp(true);
    } else {
      updateStatusMutation.mutate({ id, status });
    }
  };

  const handleDeleteClick = (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this invoice?",
      onOk: async () => {
        await deleteInvoiceMutation.mutateAsync(id);
      },
    });
  };

  const handleDownloadInvoice = async (record, options = {}) => {
    const isCreditInvoice = Boolean(
      options.isCreditInvoice ||
      record?.isCreditInvoice ||
      record?.documentType === "credit"
    );
    const { creditOverrides = null } = options;
    if (!record?._id && !record?.invoiceNo) {
      toast.error("Invoice not found");
      return;
    }
    setDownloadingInvoices((prev) => ({
      ...prev,
      [record._id || record.invoiceNo]: true,
    }));

    try {
      // Fetch invoice details by invoiceNo when available, otherwise by _id
      let inv = record;
      if (record?.invoiceNo) {
        const resp = await api.get(`/invoice/search`, { params: { invoiceNo: String(record.invoiceNo).trim() } });
        inv = resp?.data || record;
      } else if (record?._id) {
        const resp = await api.get(`/invoice/${record._id}`);
        inv = resp?.data || record;
      }

      const invoiceDate = dayjs(inv?.invoiceDate);
      const dueDate = dayjs(inv?.dueDate);
      const paymentWithin = typeof inv?.dueDays === 'number' && !isNaN(inv.dueDays)
        ? inv.dueDays
        : Math.max(0, dueDate.diff(invoiceDate, "day"));

      const externalInvoice = isExternalInvoice(inv);
      const client = externalInvoice
        ? {
          name: inv?.name || "External Customer",
          address: "",
          postalCode: "",
          city: "",
          country: "",
          vatNumber: "",
        }
        : await getUser(inv?.userId);

      const sourceItems = creditOverrides?.items || inv?.items || [];
      const services = sourceItems.map(i => ({
        description: i?.description || "",
        numberOfItem: Number(i?.qty || 0),
        exclVAT: Number(i?.price || 0),
      }));
      const subTotal = creditOverrides
        ? Number(creditOverrides.subTotal)
        : services.reduce((sum, s) => sum + Number(s.numberOfItem) * Number(s.exclVAT), 0);
      const vatAmount = creditOverrides
        ? Number(creditOverrides.vatAmount)
        : (typeof inv?.vatAmount === 'number'
          ? inv.vatAmount
          : (typeof inv?.vatPercentage === 'number' ? Number(((subTotal * inv.vatPercentage) / 100).toFixed(2)) : 0));
      const insurance = creditOverrides
        ? Number(creditOverrides.insurance || 0)
        : (typeof inv?.insuranceAmount === 'number' ? inv.insuranceAmount : 0);
      const total = creditOverrides
        ? Number(creditOverrides.total)
        : (typeof inv?.total === 'number' ? inv.total : Number(subTotal) + Number(vatAmount) + Number(insurance));

      let quoteData = null;
      if (inv?.shipmentId) {
        try {
          quoteData = await getQuote(inv.shipmentId);
        } catch (e) {
          console.warn("Could not fetch quote for shipment:", inv.shipmentId, e);
        }
      }

      let discount = null;
      if (inv && Object.prototype.hasOwnProperty.call(inv, 'discount')) {
        discount = inv.discount;
      } else if (inv?.discount !== undefined) {
        discount = inv.discount;
      } else {
        discount = quoteData?.discount || quoteData?.tradeOSData?.discount || null;
        if (!discount && !isCreditInvoice) {
          const expectedBeforeDiscount = Number((Number(subTotal) + Number(vatAmount) + Number(insurance)).toFixed(2));
          const diff = Number((expectedBeforeDiscount - Number(total)).toFixed(2));
          if (diff > 0.01) {
            const pct = Number(subTotal) > 0 ? (diff / Number(subTotal)) * 100 : 0;
            const isRound = Math.abs(pct - Math.round(pct)) < 0.05;
            discount = {
              type: isRound ? "percentage" : "fixed",
              value: isRound ? Math.round(pct) : diff,
              amount: diff,
            };
          }
        }

        if (discount && inv?._id) {
          api.put(`/invoice/${inv._id}`, { discount }).catch(() => {});
        }
      }

      const shipment = {
        services,
        subTotal,
        discount,
        vatAmount,
        vatPercentage: creditOverrides?.vatPercentage ?? inv?.vatPercentage,
        insurance,
        total,
        currency: inv?.currency || 'USD',
        orderNumber: inv?.orderNumber || '',
        isInsuranceChecked: creditOverrides
          ? Math.abs(Number(creditOverrides.insurance || 0)) > 0
          : !!inv?.isInsuranceChecked,
      };

      const dataToSend = {
        ...inv,
        discount,
        paymentWithin,
        client,
        shipment,
        items: sourceItems,
        insuranceAmount: shipment.insurance,
        vatAmount: shipment.vatAmount,
        collectionLocation: inv?.collectionLocation || "",
        deliveryLocation: inv?.deliveryLocation || "",
        billToText: inv?.billToText || "",
        isCreditInvoice,
        documentType: isCreditInvoice ? "credit" : "invoice",
      };

      const blob = await InvoicePDF(dataToSend);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const prefix = isCreditInvoice ? "CreditInvoice" : "Invoice";
      const fileName = dataToSend?.invoiceNo
        ? `${prefix}_${dataToSend.invoiceNo}.pdf`
        : `${prefix}_${dataToSend._id || "unknown"}.pdf`;
      link.download = fileName;
      link.click();

      if (isCreditInvoice && creditOverrides) {
        setCreditModalOpen(false);
        setCreateCreditInvoiceMode(false);
        setSelectedInvoice(null);
      }
    } catch (error) {
      toast.error(
        (isCreditInvoice ? "Credit invoice not downloaded: " : "Invoice not downloaded: ") +
        (error?.response?.data?.message || error.message)
      );
    } finally {
      setDownloadingInvoices((prev) => ({
        ...prev,
        [record._id || record.invoiceNo]: false,
      }));
    }
  };

  const importInvoiceFile = async (file) => {
    try {
      setImportLoading(true);
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array", cellDates: true });
      const sheetName = workbook.SheetNames[0];

      if (!sheetName) {
        toast.error("Import file has no sheets");
        return Upload.LIST_IGNORE;
      }

      const rawRows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
        header: 1,
        defval: "",
        raw: false,
      });

      // Find the first row that contains at least one non-empty value (this will be our header row)
      let headerRowIndex = -1;
      for (let i = 0; i < rawRows.length; i++) {
        const row = rawRows[i];
        if (Array.isArray(row) && row.some(val => val !== undefined && val !== null && String(val).trim() !== "")) {
          headerRowIndex = i;
          break;
        }
      }

      if (headerRowIndex === -1) {
        toast.error("Import file has no invoice rows");
        return Upload.LIST_IGNORE;
      }

      const headers = rawRows[headerRowIndex].map(h => String(h || "").trim());
      const rows = [];

      for (let i = headerRowIndex + 1; i < rawRows.length; i++) {
        const row = rawRows[i];
        // Skip completely empty rows
        if (!Array.isArray(row) || !row.some(val => val !== undefined && val !== null && String(val).trim() !== "")) {
          continue;
        }

        const rowObj = {};
        headers.forEach((header, index) => {
          if (header) {
            rowObj[header] = row[index] !== undefined ? row[index] : "";
          }
        });
        rows.push(rowObj);
      }

      if (!rows.length) {
        toast.error("Import file has no invoice rows");
        return Upload.LIST_IGNORE;
      }

      const invoices = rows.map((row) => {
        const normalized = normalizeImportRow(row);
        return normalized;
      });
      const response = await api.post(
        "/invoice/import",
        { invoices },
        { headers: { authorization: `Bearer ${token}` } }
      );

      queryClient.invalidateQueries(["invoices"]);
      toast.success(`${response?.data?.importedCount || invoices.length} invoice(s) imported`);
    } catch (error) {
      const serverErrors = error?.response?.data?.errors;
      const duplicates = error?.response?.data?.duplicates;
      if (Array.isArray(serverErrors) && serverErrors.length) {
        Modal.error({
          title: "Invoice import failed",
          content: (
            <div className="max-h-72 overflow-y-auto">
              {serverErrors.slice(0, 20).map((item, index) => (
                <p key={index} className="mb-1">{item}</p>
              ))}
              {serverErrors.length > 20 && <p>...and {serverErrors.length - 20} more error(s)</p>}
            </div>
          ),
          width: 640,
        });
      } else if (Array.isArray(duplicates) && duplicates.length) {
        Modal.error({
          title: "Duplicate invoice numbers",
          content: duplicates.join(", "),
          width: 640,
        });
      } else {
        toast.error(error?.response?.data?.message || "Invoice import failed");
      }
    } finally {
      setImportLoading(false);
    }

    return Upload.LIST_IGNORE;
  };

  const handleImportFile = (file) => {
    Modal.confirm({
      title: "Import invoices?",
      content: (
        <div>
          <p className="mb-2">Selected file: {file?.name}</p>
          <p className="mb-2">
            Expected columns: {INVOICE_IMPORT_TEMPLATE_HEADERS.join(", ")}.
          </p>
          <p>Invoice numbers are imported only from the file. Blank invoice numbers stay blank.</p>
        </div>
      ),
      okText: "Import",
      cancelText: "Cancel",
      centered: true,
      onOk: () => importInvoiceFile(file),
    });

    return Upload.LIST_IGNORE;
  };

  const columns = [
    {
      title: "User Name",
      dataIndex: "userName",
      render: (_, record) => {
        const mongoName = String(record?.name || "").trim();
        const displayName = mongoName || userDetails[record?.userId]?.name || "N/A";

        return (
          <div className="flex items-center gap-2">
            {hasEditableMongoName(record) && userIsAdmin && (
              <button
                type="button"
                title="Edit MongoDB name"
                aria-label={`Edit name for ${displayName}`}
                className="shrink-0 text-[#053F92] transition hover:text-indigo-600"
                onClick={() => {
                  setSelectedInvoice(record);
                  setEditModalOpen(true);
                }}
              >
                <HiOutlinePencilSquare size={18} />
              </button>
            )}
            <span>{displayName}</span>
          </div>
        );
      },
    },
    {
      title: "Order Number",
      dataIndex: "orderNumber",
      render: (_, record) => (record?.orderNumber ? record?.orderNumber : "-"),
    },
    {
      title: "Invoice No.",
      dataIndex: "invoiceNo",
      render: (text, record) => {
        return text || "-";
      },
      sorter: (a, b) => {
        const numA = String(a.invoiceNo || "");
        const numB = String(b.invoiceNo || "");
        return numA.localeCompare(numB, undefined, { numeric: true, sensitivity: "base" });
      },
    },
    {
      title: "Invoice Date",
      dataIndex: ["date"], // This assumes 'date' is at the top level
      render: (date, record) => {
        const invoiceDate =
          record.date || record.invoiceDate || record.createdAt;
        if (invoiceDate) {
          return dayjs(invoiceDate).format("DD MMM YYYY");
        }
        return "N/A";
      },
      sorter: (a, b) => {
        const dateA = a.date || a.invoiceDate || a.createdAt;
        const dateB = b.date || b.invoiceDate || b.createdAt;
        return new Date(dateA) - new Date(dateB);
      },
      defaultSortOrder: "descend",
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      render: (_, record) => {
        if (!record?.dueDate) return "N/A";
        const parsedDueDate = dayjs(record.dueDate);
        if (!parsedDueDate.isValid()) return "N/A";

        const today = dayjs().startOf("day");
        const isPaid = record.status?.toLowerCase() === "paid";
        const isCredit = isCreditInvoiceRecord(record);
        const isPastDue = parsedDueDate.startOf("day").isBefore(today);
        const isOverdue = !isPaid && !isCredit && isPastDue;
        const daysOverdue = isOverdue ? today.diff(parsedDueDate.startOf("day"), "day") : 0;

        return (
          <div className="flex flex-col">
            <span>{parsedDueDate.format("DD MMM YYYY")}</span>
            {isOverdue && daysOverdue > 0 && (
              <span className="text-[11px] text-red-500 font-medium whitespace-nowrap">
                Overdue by {daysOverdue} {daysOverdue === 1 ? "day" : "days"}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Invoice Amount",
      dataIndex: "invoiceAmount",
      render: (_, record) =>
        record?.total !== undefined && record?.total !== null
          ? formatInvoiceCurrency(
            getCreditSignedAmount(record, record.total),
            record.currency
          )
          : "N/A",
    },
    {
      title: "Outstanding Amount",
      dataIndex: "outstandingAmount",
      render: (_, record) => {
        // Check if remaining is explicitly null/undefined or if it exists (including zero)
        return record?.remaining !== undefined && record?.remaining !== null
          ? formatInvoiceCurrency(
            getCreditSignedAmount(record, record.remaining),
            record.currency
          )
          : "N/A";
      },
    },
    {
      title: "Paid Amount",
      dataIndex: "paidAmount",
      render: (_, record) => {
        return record?.paid !== undefined && record?.paid !== null
          ? formatInvoiceCurrency(
            getCreditSignedAmount(record, record.paid),
            record.currency
          )
          : "N/A";
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      render: (text) => (
        <div
          style={{
            maxWidth: '250px',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
          }}
        >
          {text}
        </div>
      ),
    },
    {
      title: "Last Payment Date",
      dataIndex: "lastPaymentDate",
      width: 155,
      render: (_, record) => {
        const dateVal = record?.lastPaymentDate;
        return user?.role?.includes("admin") ? (
          <DatePicker
            style={{ width: 145, minWidth: 145 }}
            allowClear={true}
            placeholder="Select date"
            disabledDate={(current) => current && current > dayjs().endOf("day")}
            value={dateVal ? dayjs(dateVal) : null}
            format="DD MMM YYYY"
            onChange={(date) => {
              updatePaymentDateMutation.mutate({
                id: record._id,
                lastPaymentDate: date ? date.startOf("day").toISOString() : null,
              });
            }}
          />
        ) : (
          dateVal ? dayjs(dateVal).format("DD MMM YYYY") : "N/A"
        );
      },
      sorter: (a, b) => {
        if (!a.lastPaymentDate) return 1;
        if (!b.lastPaymentDate) return -1;
        return new Date(a.lastPaymentDate) - new Date(b.lastPaymentDate);
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      width: "145px",
      render: (_, record) =>
        user?.role?.includes("admin") ? (
          <Select
            className={`invoice-status-select ${(record?.status || "").toLowerCase() === "paid"
              ? "invoice-status-select--paid"
              : (record?.status || "").toLowerCase() === "not paid"
                ? "invoice-status-select--not-paid"
                : (record?.status || "").toLowerCase() === "partially paid"
                  ? "invoice-status-select--partially-paid"
                  : ""
              } w-full`}
            popupClassName="invoice-status-dropdown"
            dropdownClassName="invoice-status-dropdown"
            dropdownStyle={{ minWidth: 145 }}
            value={record?.status || undefined}
            onSelect={(value) => {
              if (value === record?.status) {
                handleStatusChange(record?._id, value, record);
              }
            }}
            onChange={(value) => {
              if (value !== record?.status) {
                handleStatusChange(record?._id, value, record);
              }
            }}
            options={[
              { value: "Paid", label: <span className="status-chip status-chip--paid">Paid</span> },
              { value: "Not Paid", label: <span className="status-chip status-chip--not-paid">Not Paid</span> },
              { value: "Partially Paid", label: <span className="status-chip status-chip--partially-paid">Partially Paid</span> },
            ]}
          />
        ) : (
          <Tag
            onClick={() => record?.status === "Partially Paid" && handleStatusChange(record?._id, "Partially Paid", record)}
            className={`capitalize text-white border-none px-3 py-1 min-w-[80px] flex items-center justify-center rounded-md text-base ${
              record?.status === "Partially Paid" ? "cursor-pointer" : ""
            } ${record?.status === "Paid"
              ? "bg-green-600"
              : record?.status === "Not Paid"
                ? "bg-red-600"
                : record?.status === "Partially Paid"
                  ? "bg-orange-600"
                  : ""
              }`}
          >
            {record?.status}
          </Tag>
        ),
      filters: [
        { text: 'Paid', value: 'paid' },
        { text: 'Not Paid', value: 'not paid' },
        { text: 'Partially Paid', value: 'partially paid' }
      ],
      onFilter: (value, record) => record.status?.toLowerCase() === value,
      filterMultiple: false
    },

    {
      title: "Actions",
      render: (_, record, index) => {
        const importInvoice = isImportInvoice(record);

        return user?.role === "user" ? (
          <button
            className="border relative p-1.5 rounded-md border-[#053F92] flex items-center gap-1 btnBg"
            onClick={() => handleDownloadInvoice(record)}
            disabled={downloadingInvoices[record._id]}
          >
            {downloadingInvoices[record._id]
              ? "Downloading..."
              : <>
                <Download /><span>Invoice</span>
              </>
            }
          </button>
        ) : (
          <Dropdown
            placement="bottomRight"
            overlay={
              <Menu>
                {!importInvoice && (
                  <>
                    <Menu.Item
                      key="edit"
                      onClick={() => {
                        setSelectedInvoice(record);
                        setEditModalOpen(true);
                      }}
                    >
                      <Space>Edit Invoice</Space>
                    </Menu.Item>
                    <Menu.Item
                      onClick={() => handleDownloadInvoice(record)}
                      key="download"
                      disabled={downloadingInvoices[record._id]}
                    >
                      <Space>
                        {downloadingInvoices[record._id]
                          ? "Downloading..."
                          : "Download Invoice"}
                      </Space>
                    </Menu.Item>
                    <Menu.Item
                      onClick={() => {
                        setCreateCreditInvoiceMode(false);
                        setSelectedInvoice(record);
                        setCreditModalOpen(true);
                      }}
                      key="credit"
                      disabled={downloadingInvoices[record._id]}
                    >
                      <Space>Credit Invoice</Space>
                    </Menu.Item>
                  </>
                )}
                <Menu.Item
                  onClick={() => handleDeleteClick(record?._id)}
                  key="delete"
                >
                  <Space>Delete</Space>
                </Menu.Item>
              </Menu>
            }
            trigger={["click"]}
          >
            <Space className="relative">
              <button className="border relative p-1 rounded-md border-[#053F92]">
                <PiDotsThreeVerticalBold size={20} />
              </button>
            </Space>
          </Dropdown>
        );
      },
    },
  ];

  const columnsForPageThree = [
    { label: "User Name", field: "userName" },
    { label: "Order Number", field: "orderNumber" },
    { label: "Invoice Number", field: "invoiceNo" },
    { label: "Invoice Date", field: "invoiceDate" },
    { label: "Due Date", field: "dueDate" },
    { label: "Days Overdue" },
    { label: "Invoice Amount", field: "invoiceAmount" },
    { label: "Amount Paid", field: "paidAmount" },
    { label: "Outstanding Amount", field: "outstandingAmount" },
    { label: "Description", field: "description" },
    { label: "Last Payment Date", field: "lastPaymentDate" },
    { label: "Status", field: "status" },
  ];

  return (
    <>
      <DashboardLayout>
        <div className="w-full h-full">
          <Header title="Invoices" />
          <div className="p-4 bg-cus-gray min-h-screen">
            <div className="p-5 rounded-[10px] bg-[#fff] w-[90%] md:w-[96%] mx-auto" style={{ border: "1px solid rgba(5, 63, 146, 0.24)" }}>
              <div className="flex justify-between items-center space-y-2 flex-wrap">
                <div className="flex w-full flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="w-full lg:max-w-[255px] p-3 flex space-x-2 items-center rounded-[5px] border-2 border-blue">
                    <Search />
                    <input
                      placeholder="Search invoices..."
                      className="outline-none w-full"
                      style={{ color: "rgba(24, 24, 35, 0.53)" }}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="flex w-full flex-col gap-3 sm:flex-row sm:justify-end lg:w-auto">
                    {userIsAdmin && (
                      <button
                        type="button"
                        className="w-full sm:w-auto flex rounded-[5px] items-center justify-center space-x-3 py-2 px-4 text-white btnBg"
                        onClick={() => {
                          setCreateInvoiceShipment(null);
                          setCreateInvoiceOpen(true);
                        }}
                      >
                        <span className="text-lg leading-none">+</span>
                        <p>Create Invoice</p>
                      </button>
                    )}
                    {userIsAdmin && (
                      <button
                        type="button"
                        className="w-full sm:w-auto flex rounded-[5px] items-center justify-center space-x-3 py-2 px-4 text-white btnBg"
                        onClick={() => {
                          setSelectedInvoice(null);
                          setCreateCreditInvoiceMode(true);
                          setCreditModalOpen(true);
                        }}
                      >
                        <span className="text-lg leading-none">−</span>
                        <p>Credit Invoice</p>
                      </button>
                    )}
                    {userIsAdmin && (
                      <Upload
                        accept=".csv,.xlsx,.xls"
                        showUploadList={false}
                        beforeUpload={handleImportFile}
                        disabled={importLoading}
                      >
                        <button
                          type="button"
                          className="w-full sm:w-auto flex rounded-[5px] items-center justify-center space-x-3 py-2 px-4 text-white btnBg disabled:opacity-60"
                          disabled={importLoading}
                        >
                          <Download />
                          <p>{importLoading ? "Importing..." : "Import Invoices"}</p>
                        </button>
                      </Upload>
                    )}
                    <button
                      className="w-full sm:w-auto flex rounded-[5px] items-center justify-center space-x-3 py-2 px-4 text-white btnBg"
                      onClick={() => setIsPopupOpen(true)}
                    >
                      <UploadOutlined />
                      <p>Export</p>
                    </button>
                  </div>
                </div>
                {isPopupOpen && (
                  <ExportCSVPopup
                    closePopup={() => setIsPopupOpen(false)}
                    Data={sortedAndFilteredData}
                    usersData={uniqueUsers}
                    userDetails={userDetails}
                    columnsToExport={columnsForPageThree}
                    exportName="Invoice"
                  />
                )}
              </div>
            </div>
            {isLoading ? (
              <Loader />
            ) : (
              <div className="p-5 flex flex-col bg-cus-gray rounded-md mt-4 space-y-2 min-h-[calc(100vh-200px)]">
                <CardsContainer invoicesData={sortedAndFilteredData} />
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4 mt-2">
                  {/* Filters on Left Side */}
                  <div className="flex flex-wrap items-center gap-3">
                    {userIsAdmin && (
                      <div className="w-full sm:w-56 md:w-64">
                        <Select
                          showSearch
                          id="userName"
                          options={uniqueUsers.map(user => ({
                            value: user.id,
                            label: user.name
                          }))}
                          placeholder="Filter by user"
                          className="w-full"
                          allowClear
                          onChange={(value) => setSelectedUser(value)}
                          value={selectedUser}
                          optionFilterProp="label"
                        />
                      </div>
                    )}

                    {/* Month Filter Dropdown */}
                    <div className="w-full sm:w-36 md:w-44">
                      <Select
                        showSearch
                        id="monthFilter"
                        options={MONTH_OPTIONS}
                        placeholder="Filter by month"
                        className="w-full"
                        allowClear
                        onChange={(value) => setSelectedMonth(value !== undefined ? value : null)}
                        value={selectedMonth}
                        optionFilterProp="label"
                      />
                    </div>

                    {/* Year Filter Dropdown */}
                    <div className="w-full sm:w-28 md:w-32">
                      <Select
                        showSearch
                        id="yearFilter"
                        options={availableYears}
                        placeholder="Filter by year"
                        className="w-full"
                        allowClear
                        onChange={(value) => setSelectedYear(value !== undefined ? value : null)}
                        value={selectedYear}
                        optionFilterProp="label"
                      />
                    </div>

                    {(selectedUser || selectedMonth !== null || selectedYear !== null) && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedUser(null);
                          setSelectedMonth(null);
                          setSelectedYear(null);
                        }}
                        className="text-xs text-gray-500 hover:text-red-600 transition-colors px-2 py-1 underline font-medium cursor-pointer"
                      >
                        Reset Filters
                      </button>
                    )}
                  </div>

                  {/* Admin Status Filter Buttons (Right Side) */}
                  {userIsAdmin && (
                    <div className="flex items-center space-x-2 bg-white p-1.5 rounded-lg border border-gray-200 shadow-sm">
                      {[
                        { key: "all", label: "All", count: filterCounts.all, activeBg: "#053f92", activeText: "#ffffff" },
                        { key: "pending", label: "Pending", count: filterCounts.pending, activeBg: "#d97706", activeText: "#ffffff" },
                        { key: "paid", label: "Paid", count: filterCounts.paid, activeBg: "#16a34a", activeText: "#ffffff" },
                        { key: "overdue", label: "Overdue", count: filterCounts.overdue, activeBg: "#dc2626", activeText: "#ffffff" },
                      ].map((btn) => {
                        const isActive = statusFilter === btn.key;
                        return (
                          <button
                            key={btn.key}
                            type="button"
                            onClick={() => setStatusFilter(btn.key)}
                            style={
                              isActive
                                ? { backgroundColor: btn.activeBg, color: btn.activeText }
                                : { backgroundColor: "transparent", color: "#4b5563" }
                            }
                            className={`flex items-center space-x-2 px-3.5 py-1.5 rounded-md text-sm font-medium transition-all duration-200 cursor-pointer ${
                              isActive ? "shadow-sm font-semibold" : "hover:bg-gray-100 hover:text-gray-900"
                            }`}
                          >
                            <span>{btn.label}</span>
                            <span
                              style={
                                isActive
                                  ? { backgroundColor: "rgba(255, 255, 255, 0.25)", color: "#ffffff" }
                                  : { backgroundColor: "#e5e7eb", color: "#374151" }
                              }
                              className="px-2 py-0.5 rounded-full text-xs font-bold transition-all duration-200"
                            >
                              {btn.count}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="bg-white rounded-md overflow-hidden">
                  <Table
                    columns={columns}
                    dataSource={sortedAndFilteredData}
                    loading={isLoading}
                    scroll={needsScroll ? { x: true } : undefined}
                    rowKey="_id"
                    rowClassName={(record) =>
                      highlightedInvoiceId?.includes(record._id)
                        ? "floating-invoice-row"
                        : ""
                    }
                  />
                </div>
              </div>
            )}

          </div>
          {showPopUp && (
            <PopUp
              visible={showPopUp}
              onClose={() => {
                setShowPopUp(false);
                setSelectedInvoice(null);
              }}
              invoiceData={selectedInvoice}
            />
          )}
          {editModalOpen && (
            <EditInvoiceModal
              open={editModalOpen}
              onClose={() => {
                setEditModalOpen(false);
                setSelectedInvoice(null);
              }}
              invoice={selectedInvoice}
              onUpdated={(updatedInvoiceId) => {
                queryClient.invalidateQueries(["invoices"]);
                const updatedInvoice = data.find(i => i._id === updatedInvoiceId);
                if (updatedInvoice) {
                  setHighlightedInvoiceId(updatedInvoice._id);
                }
              }}
            />
          )}
          {creditModalOpen && (
            <CreditInvoiceModal
              open={creditModalOpen}
              createMode={createCreditInvoiceMode}
              onClose={() => {
                setCreditModalOpen(false);
                setCreateCreditInvoiceMode(false);
                setSelectedInvoice(null);
              }}
              invoice={createCreditInvoiceMode ? null : selectedInvoice}
              downloading={
                !!(
                  selectedInvoice?._id &&
                  downloadingInvoices[selectedInvoice._id]
                )
              }
              onCreated={(createdInvoice) => {
                queryClient.invalidateQueries(["invoices"]);
                if (createdInvoice?._id) {
                  setHighlightedInvoiceId([createdInvoice._id]);
                }
              }}
              onDownload={(creditOverrides, invoiceForPdf) =>
                handleDownloadInvoice(invoiceForPdf || selectedInvoice, {
                  isCreditInvoice: true,
                  creditOverrides,
                })
              }
            />
          )}
          {createInvoiceOpen && (
            <InvoiceModal
              modalOpen={createInvoiceOpen}
              setModalOpen={(open) => {
                setCreateInvoiceOpen(open);
                if (!open) {
                  setCreateInvoiceShipment(null);
                }
              }}
              shipmentData={createInvoiceShipment}
              setShipmentData={setCreateInvoiceShipment}
              externalInvoiceMode
              onInvoiceCreated={(createdInvoice) => {
                queryClient.invalidateQueries(["invoices"]);
                if (createdInvoice?._id) {
                  setHighlightedInvoiceId([createdInvoice._id]);
                }
              }}
            />
          )}
        </div>
      </DashboardLayout>
    </>
  );
};

export default Invoices;
